/**
 * Curated list of popular Google Fonts with their available variants
 * Font URLs use: https://fonts.googleapis.com/css2?family={name}:wght@{weights}&display=swap
 */

export interface GoogleFontVariant {
  weight: number;
  label: string;
  style?: "normal" | "italic";
}

export interface GoogleFont {
  name: string;
  category: "serif" | "sans-serif" | "display" | "handwriting" | "monospace";
  variants: GoogleFontVariant[];
  popular?: boolean;
}

// Standard weight labels
const weightLabels: Record<number, string> = {
  100: "Thin",
  200: "Extra Light",
  300: "Light",
  400: "Regular",
  500: "Medium",
  600: "Semi Bold",
  700: "Bold",
  800: "Extra Bold",
  900: "Black",
};

// Helper to create variants array
const createVariants = (weights: number[]): GoogleFontVariant[] =>
  weights.map((weight) => ({
    weight,
    label: weightLabels[weight] || `Weight ${weight}`,
  }));

export const googleFonts: GoogleFont[] = [
  // === SANS-SERIF ===
  {
    name: "Inter",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
    popular: true,
  },
  {
    name: "Roboto",
    category: "sans-serif",
    variants: createVariants([100, 300, 400, 500, 700, 900]),
    popular: true,
  },
  {
    name: "Open Sans",
    category: "sans-serif",
    variants: createVariants([300, 400, 500, 600, 700, 800]),
    popular: true,
  },
  {
    name: "Lato",
    category: "sans-serif",
    variants: createVariants([100, 300, 400, 700, 900]),
    popular: true,
  },
  {
    name: "Montserrat",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
    popular: true,
  },
  {
    name: "Poppins",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
    popular: true,
  },
  {
    name: "Nunito",
    category: "sans-serif",
    variants: createVariants([200, 300, 400, 500, 600, 700, 800, 900]),
    popular: true,
  },
  {
    name: "Nunito Sans",
    category: "sans-serif",
    variants: createVariants([200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Source Sans 3",
    category: "sans-serif",
    variants: createVariants([200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Raleway",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
    popular: true,
  },
  {
    name: "Work Sans",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Oswald",
    category: "sans-serif",
    variants: createVariants([200, 300, 400, 500, 600, 700]),
    popular: true,
  },
  {
    name: "Rubik",
    category: "sans-serif",
    variants: createVariants([300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Quicksand",
    category: "sans-serif",
    variants: createVariants([300, 400, 500, 600, 700]),
  },
  {
    name: "Mulish",
    category: "sans-serif",
    variants: createVariants([200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Barlow",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Barlow Condensed",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Kanit",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Manrope",
    category: "sans-serif",
    variants: createVariants([200, 300, 400, 500, 600, 700, 800]),
  },
  {
    name: "DM Sans",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Outfit",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Plus Jakarta Sans",
    category: "sans-serif",
    variants: createVariants([200, 300, 400, 500, 600, 700, 800]),
  },
  {
    name: "Figtree",
    category: "sans-serif",
    variants: createVariants([300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Cabin",
    category: "sans-serif",
    variants: createVariants([400, 500, 600, 700]),
  },
  {
    name: "Karla",
    category: "sans-serif",
    variants: createVariants([200, 300, 400, 500, 600, 700, 800]),
  },
  {
    name: "Archivo",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Josefin Sans",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700]),
  },
  {
    name: "Lexend",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Exo 2",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Titillium Web",
    category: "sans-serif",
    variants: createVariants([200, 300, 400, 600, 700, 900]),
  },
  {
    name: "Sora",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800]),
  },
  {
    name: "Space Grotesk",
    category: "sans-serif",
    variants: createVariants([300, 400, 500, 600, 700]),
  },
  {
    name: "Albert Sans",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Be Vietnam Pro",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Asap",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Ubuntu",
    category: "sans-serif",
    variants: createVariants([300, 400, 500, 700]),
    popular: true,
  },
  {
    name: "Dosis",
    category: "sans-serif",
    variants: createVariants([200, 300, 400, 500, 600, 700, 800]),
    popular: true,
  },
  {
    name: "Noto Sans",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
    popular: true,
  },
  {
    name: "Noto Sans Display",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Fira Sans",
    category: "sans-serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "PT Sans",
    category: "sans-serif",
    variants: createVariants([400, 700]),
  },
  {
    name: "PT Sans Narrow",
    category: "sans-serif",
    variants: createVariants([400, 700]),
  },
  {
    name: "Mukta",
    category: "sans-serif",
    variants: createVariants([200, 300, 400, 500, 600, 700, 800]),
  },
  {
    name: "Hind",
    category: "sans-serif",
    variants: createVariants([300, 400, 500, 600, 700]),
  },
  {
    name: "Rajdhani",
    category: "sans-serif",
    variants: createVariants([300, 400, 500, 600, 700]),
  },
  {
    name: "Varela Round",
    category: "sans-serif",
    variants: createVariants([400]),
  },
  {
    name: "Comic Neue",
    category: "sans-serif",
    variants: createVariants([300, 400, 700]),
  },

  // === SERIF ===
  {
    name: "Playfair Display",
    category: "serif",
    variants: createVariants([400, 500, 600, 700, 800, 900]),
    popular: true,
  },
  {
    name: "Merriweather",
    category: "serif",
    variants: createVariants([300, 400, 700, 900]),
    popular: true,
  },
  {
    name: "Lora",
    category: "serif",
    variants: createVariants([400, 500, 600, 700]),
    popular: true,
  },
  {
    name: "PT Serif",
    category: "serif",
    variants: createVariants([400, 700]),
  },
  {
    name: "Noto Serif",
    category: "serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Source Serif 4",
    category: "serif",
    variants: createVariants([200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Libre Baskerville",
    category: "serif",
    variants: createVariants([400, 700]),
  },
  {
    name: "Crimson Text",
    category: "serif",
    variants: createVariants([400, 600, 700]),
  },
  {
    name: "Crimson Pro",
    category: "serif",
    variants: createVariants([200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Bitter",
    category: "serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Cormorant Garamond",
    category: "serif",
    variants: createVariants([300, 400, 500, 600, 700]),
  },
  {
    name: "EB Garamond",
    category: "serif",
    variants: createVariants([400, 500, 600, 700, 800]),
  },
  {
    name: "DM Serif Display",
    category: "serif",
    variants: createVariants([400]),
  },
  {
    name: "Spectral",
    category: "serif",
    variants: createVariants([200, 300, 400, 500, 600, 700, 800]),
  },
  {
    name: "Cardo",
    category: "serif",
    variants: createVariants([400, 700]),
  },
  {
    name: "Libre Caslon Text",
    category: "serif",
    variants: createVariants([400, 700]),
  },
  {
    name: "Bodoni Moda",
    category: "serif",
    variants: createVariants([400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Fraunces",
    category: "serif",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Cormorant",
    category: "serif",
    variants: createVariants([300, 400, 500, 600, 700]),
  },
  {
    name: "Cinzel",
    category: "serif",
    variants: createVariants([400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Playfair Display SC",
    category: "serif",
    variants: createVariants([400, 700, 900]),
  },

  // === DISPLAY ===
  {
    name: "Bebas Neue",
    category: "display",
    variants: createVariants([400]),
    popular: true,
  },
  {
    name: "Abril Fatface",
    category: "display",
    variants: createVariants([400]),
    popular: true,
  },
  {
    name: "Righteous",
    category: "display",
    variants: createVariants([400]),
  },
  {
    name: "Alfa Slab One",
    category: "display",
    variants: createVariants([400]),
  },
  {
    name: "Fredoka",
    category: "display",
    variants: createVariants([300, 400, 500, 600, 700]),
  },
  {
    name: "Comfortaa",
    category: "display",
    variants: createVariants([300, 400, 500, 600, 700]),
  },
  {
    name: "Lobster",
    category: "display",
    variants: createVariants([400]),
  },
  {
    name: "Staatliches",
    category: "display",
    variants: createVariants([400]),
  },
  {
    name: "Bungee",
    category: "display",
    variants: createVariants([400]),
  },
  {
    name: "Monoton",
    category: "display",
    variants: createVariants([400]),
  },
  {
    name: "Black Ops One",
    category: "display",
    variants: createVariants([400]),
  },
  {
    name: "Permanent Marker",
    category: "display",
    variants: createVariants([400]),
  },
  {
    name: "Russo One",
    category: "display",
    variants: createVariants([400]),
  },
  {
    name: "Fugaz One",
    category: "display",
    variants: createVariants([400]),
  },
  {
    name: "Bangers",
    category: "display",
    variants: createVariants([400]),
  },
  {
    name: "Anton",
    category: "display",
    variants: createVariants([400]),
    popular: true,
  },
  {
    name: "Creepster",
    category: "display",
    variants: createVariants([400]),
  },
  {
    name: "Orbitron",
    category: "display",
    variants: createVariants([400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Audiowide",
    category: "display",
    variants: createVariants([400]),
  },
  {
    name: "Bungee Inline",
    category: "display",
    variants: createVariants([400]),
  },
  {
    name: "Bungee Shade",
    category: "display",
    variants: createVariants([400]),
  },
  {
    name: "Faster One",
    category: "display",
    variants: createVariants([400]),
  },
  {
    name: "Press Start 2P",
    category: "display",
    variants: createVariants([400]),
  },

  // === HANDWRITING ===
  {
    name: "Dancing Script",
    category: "handwriting",
    variants: createVariants([400, 500, 600, 700]),
    popular: true,
  },
  {
    name: "Pacifico",
    category: "handwriting",
    variants: createVariants([400]),
    popular: true,
  },
  {
    name: "Caveat",
    category: "handwriting",
    variants: createVariants([400, 500, 600, 700]),
  },
  {
    name: "Great Vibes",
    category: "handwriting",
    variants: createVariants([400]),
  },
  {
    name: "Satisfy",
    category: "handwriting",
    variants: createVariants([400]),
  },
  {
    name: "Sacramento",
    category: "handwriting",
    variants: createVariants([400]),
  },
  {
    name: "Kalam",
    category: "handwriting",
    variants: createVariants([300, 400, 700]),
  },
  {
    name: "Shadows Into Light",
    category: "handwriting",
    variants: createVariants([400]),
  },
  {
    name: "Indie Flower",
    category: "handwriting",
    variants: createVariants([400]),
  },
  {
    name: "Amatic SC",
    category: "handwriting",
    variants: createVariants([400, 700]),
  },
  {
    name: "Patrick Hand",
    category: "handwriting",
    variants: createVariants([400]),
  },
  {
    name: "Courgette",
    category: "handwriting",
    variants: createVariants([400]),
  },
  {
    name: "Shadows Into Light Two",
    category: "handwriting",
    variants: createVariants([400]),
    popular: true,
  },
  {
    name: "Caveat Brush",
    category: "handwriting",
    variants: createVariants([400]),
  },

  // === MONOSPACE ===
  {
    name: "Fira Code",
    category: "monospace",
    variants: createVariants([300, 400, 500, 600, 700]),
    popular: true,
  },
  {
    name: "JetBrains Mono",
    category: "monospace",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800]),
    popular: true,
  },
  {
    name: "Source Code Pro",
    category: "monospace",
    variants: createVariants([200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Roboto Mono",
    category: "monospace",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700]),
  },
  {
    name: "IBM Plex Mono",
    category: "monospace",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700]),
  },
  {
    name: "Space Mono",
    category: "monospace",
    variants: createVariants([400, 700]),
  },
  {
    name: "Inconsolata",
    category: "monospace",
    variants: createVariants([200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Ubuntu Mono",
    category: "monospace",
    variants: createVariants([400, 700]),
  },
  {
    name: "Noto Sans Mono",
    category: "monospace",
    variants: createVariants([100, 200, 300, 400, 500, 600, 700, 800, 900]),
  },
  {
    name: "Courier Prime",
    category: "monospace",
    variants: createVariants([400, 700]),
  },
  {
    name: "Fira Mono",
    category: "monospace",
    variants: createVariants([400, 500, 700]),
  },
];

/**
 * Build Google Fonts CSS URL for a specific font and weight
 */
export const buildGoogleFontUrl = (
  fontName: string,
  weights: number[] = [500]
): string => {
  const encodedName = fontName.replace(/ /g, "+");
  const weightStr = weights.join(";");
  return `https://fonts.googleapis.com/css2?family=${encodedName}:wght@${weightStr}&display=swap`;
};

/**
 * Build Google Fonts CSS URL for multiple fonts
 */
export const buildGoogleFontsUrl = (
  fonts: Array<{ name: string; weights: number[] }>
): string => {
  const familyParams = fonts
    .map((font) => {
      const encodedName = font.name.replace(/ /g, "+");
      const weightStr = font.weights.join(";");
      return `family=${encodedName}:wght@${weightStr}`;
    })
    .join("&");
  return `https://fonts.googleapis.com/css2?${familyParams}&display=swap`;
};

/**
 * Get font preview URL (loads just the font for preview)
 */
export const getFontPreviewUrl = (fontName: string): string => {
  return buildGoogleFontUrl(fontName, [400, 700]);
};

/**
 * Parse weight from a Google Fonts URL
 * Returns the first weight found, or 500 as default
 */
export const parseWeightFromGoogleFontUrl = (url: string): number => {
  // Match patterns like wght@400 or wght@400;500;700
  const match = url.match(/wght@(\d+)/);
  if (match) {
    return parseInt(match[1], 10);
  }
  return 500;
};

/**
 * Parse font name from a Google Fonts URL
 */
export const parseFontNameFromGoogleFontUrl = (url: string): string | null => {
  // Match patterns like family=Open+Sans or family=Crimson+Pro
  const match = url.match(/family=([^:&]+)/);
  if (match) {
    return match[1].replace(/\+/g, " ");
  }
  return null;
};

/**
 * Rebuild a Google Fonts URL with a new weight
 */
export const rebuildGoogleFontUrlWithWeight = (
  url: string,
  newWeight: number
): string => {
  // Replace existing weight with new weight
  // Handle both single weight (wght@400) and multiple weights (wght@400;500)
  return url.replace(/wght@[\d;]+/, `wght@${newWeight}`);
};

/**
 * Get available weights for a font by name
 */
export const getAvailableWeightsForFont = (
  fontName: string
): GoogleFontVariant[] => {
  const font = googleFonts.find(
    (f) => f.name.toLowerCase() === fontName.toLowerCase()
  );
  return font?.variants || [{ weight: 500, label: "Medium" }];
};

// Track preload state
let preloadPromise: Promise<void> | null = null;

/**
 * Preload all Google Fonts for preview (single batched request)
 * Returns a promise that resolves when fonts are loaded
 */
export const preloadAllGoogleFonts = (): Promise<void> => {
  // Return existing promise if already loading/loaded
  if (preloadPromise) {
    return preloadPromise;
  }

  preloadPromise = new Promise((resolve) => {
    // Build batched URL for all fonts with weights 400 and 500 (for preview)
    // This ensures Medium (500) weight renders correctly for fonts like Crimson Pro
    const fontsToLoad = googleFonts.map((font) => ({
      name: font.name,
      weights: [400, 500],
    }));

    const url = buildGoogleFontsUrl(fontsToLoad);

    // Create and inject link element
    const link = document.createElement("link");
    link.href = url;
    link.rel = "stylesheet";

    link.onload = () => {
      resolve();
    };

    link.onerror = () => {
      // Resolve anyway to not block the UI
      console.warn("Failed to preload Google Fonts");
      resolve();
    };

    document.head.appendChild(link);
  });

  return preloadPromise;
};
